/*
 * Tradução automática do conteúdo.
 *
 * O servidor manda, junto com cada postagem e tópico, a tradução para o idioma
 * de quem lê (quando já existe) ou a marca `canelaTranslatable` (falta
 * traduzir). A página desenha o original na hora e, logo depois, pede as que
 * faltam em lote (POST /canela/ai/translate) — a leitura nunca espera o modelo.
 *
 * A tradução entra pelo próprio modelo (`contentHtml()` e `title()`), então
 * aparece em todo lugar que mostra o post ou o título; o 文A à esquerda da
 * data alterna aquela postagem (na primeira, também o título do tópico), e o
 * 文A do topo da linha do tempo abre as "Preferências de idioma do conteúdo":
 * os idiomas que a pessoa lê no original e a tradução
 * automática (na conta, ou neste navegador para visitantes). Enquanto uma postagem
 * está sendo traduzida, um ícone girando fica no lugar do 文A. Editar o
 * título sempre parte do título original.
 */
import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import Post from 'flarum/common/models/Post';
import Discussion from 'flarum/common/models/Discussion';
import CommentPost from 'flarum/forum/components/CommentPost';
import DiscussionHero from 'flarum/forum/components/DiscussionHero';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import Icon from 'flarum/common/components/Icon';
import Button from 'flarum/common/components/Button';
import Tooltip from 'flarum/common/components/Tooltip';
import classList from 'flarum/common/utils/classList';
import { aiApiUrl } from './state';
import { displayNames } from '../../utils/intl';
const STORAGE_KEY = 'canela.translation.showOriginal';
/** Quantas rodadas seguidas o fórum pede o que voltou "pendente". */
const MAX_ROUNDS = 6;
const BATCH = 20;
function info() {
  const value = app.forum?.attribute('canelaAiTranslation');
  return value && value.locale ? value : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Original ou tradução
// ─────────────────────────────────────────────────────────────────────────────

/** Os itens que a pessoa virou um a um, ao contrário do modo do tópico. */
const flipped = new Set();
/** Ligado enquanto o título original precisa sair do modelo (renomear). */
let bypass = 0;

// ─────────────────────────────────────────────────────────────────────────────
// Preferências de idioma do conteúdo (o modal do 文A da linha do tempo)
// ─────────────────────────────────────────────────────────────────────────────

const UNDERSTOOD_KEY = 'canela.translation.understood';
function readStorage(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeStorage(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Navegação privada: vale só nesta página.
  }
}

/** Os idiomas que a pessoa lê sem tradução (na conta; no navegador, para visitantes). */
export function understoodLocales() {
  const user = app.session.user;
  if (user) {
    const value = user.preferences()?.canelaUnderstoodLocales;
    return Array.isArray(value) ? value.filter(code => typeof code === 'string') : [];
  }
  try {
    const value = JSON.parse(readStorage(UNDERSTOOD_KEY) || '[]');
    return Array.isArray(value) ? value.filter(code => typeof code === 'string') : [];
  } catch {
    return [];
  }
}

/** "Traduzir automaticamente": desligado, o tópico abre no original. */
export function autoTranslate() {
  const user = app.session.user;
  if (user) return user.preferences()?.canelaAutoTranslate !== false;

  // O "mostrar original" que este navegador já guardava vale como desligado.
  return readStorage(STORAGE_KEY) !== '1';
}
export function saveContentLanguagePreferences(understood, auto) {
  const user = app.session.user;
  flipped.clear();
  if (user) return user.savePreferences({
    canelaUnderstoodLocales: understood,
    canelaAutoTranslate: auto
  }).then(() => m.redraw());
  writeStorage(UNDERSTOOD_KEY, JSON.stringify(understood));
  writeStorage(STORAGE_KEY, auto ? '0' : '1');
  m.redraw();
  return Promise.resolve();
}

/** "pt-BR" e "pt" contam como o mesmo idioma: quem entende português lê os dois. */
function baseLanguage(code) {
  return code.split(/[-_]/)[0].toLowerCase();
}
function understood(locale) {
  if (!locale) return false;
  const base = baseLanguage(locale);
  return understoodLocales().some(code => baseLanguage(code) === base);
}
function itemLocale(kind, id) {
  if (kind === 'p') return app.store.getById('posts', id)?.attribute('canelaLocale') ?? null;
  return app.store.getById('discussions', id)?.attribute('canelaTitleLocale') ?? null;
}

/** Sem virar nada à mão, o item sai no original? */
function originalByDefault(kind, id) {
  return !autoTranslate() || understood(itemLocale(kind, id));
}
function showing(kind, id) {
  if (bypass > 0 || !id) return false;
  return originalByDefault(kind, id) ? flipped.has(`${kind}${id}`) : !flipped.has(`${kind}${id}`);
}

/** Vira juntos (o 文A da primeira postagem vira também o título). */
function flip(keys) {
  keys.forEach(key => {
    if (flipped.has(key)) flipped.delete(key);else flipped.add(key);
  });
  m.redraw();
}

/** O valor original do modelo, sem a tradução (para editar e comparar). */
export function withOriginal(callback) {
  bypass++;
  try {
    return callback();
  } finally {
    bypass--;
  }
}
function postTranslation(post) {
  return post.attribute('canelaTranslation') || null;
}
function titleTranslation(discussion) {
  return discussion.attribute('canelaTitleTranslation') || null;
}

/** "inglês" → "Inglês", no idioma da interface. */
function languageName(code) {
  if (!code) return '';
  try {
    const name = displayNames([app.translator.getLocale() || 'en'], {
      type: 'language'
    }).of(code) || code;
    return name.charAt(0).toLocaleUpperCase() + name.slice(1);
  } catch {
    return code;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Pedidos (em lote, depois de desenhar)
// ─────────────────────────────────────────────────────────────────────────────

const queue = {
  posts: new Map(),
  discussions: new Map()
};
/** Já pedidos nesta página, pela versão do conteúdo: não pede duas vezes o mesmo. */
const asked = new Set();
/** Postagens na fila ou no modelo agora: mostram o "traduzindo…". */
const working = new Set();
let timer = 0;
let rounds = 0;
function versionKey(kind, model) {
  const version = kind === 'p' ? String(model.editedAt()?.getTime() || '') : String(model.attribute('title') || '');
  return `${kind}${model.id()}:${version}`;
}
function enqueue(kind, model) {
  if (!model || !info()) return;
  const flag = kind === 'p' ? 'canelaTranslatable' : 'canelaTitleTranslatable';
  if (!model.attribute(flag)) return;

  // Nada de gastar o modelo com o que a pessoa vai ler no original: tradução
  // automática desligada, ou um idioma que ela entende.
  if (!autoTranslate() || understood(model.attribute(kind === 'p' ? 'canelaLocale' : 'canelaTitleLocale'))) return;
  const key = versionKey(kind, model);
  if (asked.has(key)) return;
  asked.add(key);
  // Um lote novo começa a contagem de novo: sem isso, depois que um tópico
  // desistia, todos os seguintes na mesma visita ganhavam uma rodada só.
  rounds = 0;
  if (kind === 'p') {
    queue.posts.set(model.id(), model);
    working.add(model.id());
  } else {
    queue.discussions.set(model.id(), model);
  }
  schedule(250);
}
function schedule(delay) {
  window.clearTimeout(timer);
  timer = window.setTimeout(flushWhenVisible, delay);
}

/** Com a aba escondida, espera ela voltar: ninguém está lendo a tradução. */
function flushWhenVisible() {
  if (!document.hidden) {
    flush();
    return;
  }
  const resume = () => {
    if (document.hidden) return;
    document.removeEventListener('visibilitychange', resume);
    flush();
  };
  document.addEventListener('visibilitychange', resume);
}

/** Espera crescente enquanto o servidor responde "pendente": 1,5 s, 3 s, 6 s, 12 s… */
function pendingDelay() {
  return Math.min(12000, 1500 * 2 ** Math.max(0, rounds - 1));
}
async function flush() {
  const posts = Array.from(queue.posts.values()).slice(0, BATCH);
  const discussions = Array.from(queue.discussions.values()).slice(0, BATCH);
  posts.forEach(post => queue.posts.delete(post.id()));
  discussions.forEach(discussion => queue.discussions.delete(discussion.id()));
  if (!posts.length && !discussions.length) return;
  let response;
  try {
    response = await app.request({
      method: 'POST',
      url: aiApiUrl('translate'),
      body: {
        posts: posts.map(post => post.id()),
        discussions: discussions.map(discussion => discussion.id())
      },
      // Falhou, fica o original: não é um erro que a pessoa precise ver.
      errorHandler: () => {}
    });
  } catch {
    posts.forEach(post => working.delete(post.id()));
    m.redraw();
    return;
  }
  let pending = false;
  /** O servidor traduziu mais uma parte de alguma postagem longa. */
  let advanced = false;
  posts.forEach(post => {
    const result = response?.posts?.[post.id()];
    if (result?.html) {
      working.delete(post.id());
      post.pushAttributes({
        canelaTranslation: result.html,
        canelaLocale: result.locale,
        canelaTranslatable: false
      });
    } else if (result?.pending) {
      pending = true;
      advanced = advanced || !!result.partial;
      queue.posts.set(post.id(), post);
    } else {
      working.delete(post.id());
    }
  });
  discussions.forEach(discussion => {
    const result = response?.discussions?.[discussion.id()];
    if (result?.title) {
      discussion.pushAttributes({
        canelaTitleTranslation: result.title,
        canelaTitleLocale: result.locale,
        canelaTitleTranslatable: false
      });
    } else if (result?.pending) {
      pending = true;
      queue.discussions.set(discussion.id(), discussion);
    }
  });
  m.redraw();

  // O que sobrou deste lote (ou ficou pendente) vai na próxima rodada; o
  // limite de rodadas segura um servidor que só responde "pendente". Uma
  // postagem longa que avançou uma parte não gasta rodada: segue logo.
  const more = queue.posts.size > 0 || queue.discussions.size > 0;
  if (!more) return;
  if (!pending || advanced || ++rounds <= MAX_ROUNDS) {
    if (advanced) rounds = 0;
    schedule(response?.retryAfter ? response.retryAfter * 1000 : advanced ? 200 : pending ? pendingDelay() : 50);
  } else {
    // Desistiu por agora: some o "traduzindo…"; o próximo carregamento tenta de novo.
    queue.posts.forEach((_post, id) => working.delete(id));
    queue.posts.clear();
    queue.discussions.clear();
    m.redraw();
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Interface
// ─────────────────────────────────────────────────────────────────────────────

/**
 * O 文A: tooltip com o idioma original e o aviso; clicar alterna. `keys` são
 * os itens que viram juntos (a postagem e, na primeira, o título).
 */
function indicator(kind, id, locale, keys = [`${kind}${id}`]) {
  const translated = showing(kind, id);
  const language = languageName(locale);
  // `mul`: a postagem mistura línguas (títulos numa, itens noutra).
  const what = locale === 'mul' ? app.translator.trans('ramon-canela.forum.ai.translation.original_mixed') : app.translator.trans(`ramon-canela.forum.ai.translation.${kind === 'p' ? 'original_language' : 'title_original_language'}`, {
    language
  });
  const action = app.translator.trans(`ramon-canela.forum.ai.translation.${translated ? 'click_to_show_original' : 'click_to_show_translation'}`);
  return m("span", {
    className: classList('CanelaTranslated', {
      'is-original': !translated
    })
  }, m("button", {
    type: "button",
    className: "CanelaTranslated-button",
    "aria-pressed": translated ? 'true' : 'false',
    "aria-label": `${what} ${action}`,
    onclick: event => {
      event.preventDefault();
      event.stopPropagation();
      flip(keys);
    }
  }, m(Icon, {
    name: "fas fa-language"
  })), m("span", {
    className: "CanelaTranslated-tip",
    role: "tooltip"
  }, m("span", {
    className: "CanelaTranslated-tipText"
  }, what, " ", action), m("span", {
    className: "CanelaTranslated-disclaimer"
  }, app.translator.trans('ramon-canela.forum.ai.translation.disclaimer'))));
}

/** O "traduzindo…": a postagem está na fila ou no modelo. */
function translating() {
  const label = app.translator.trans('ramon-canela.forum.ai.translation.translating');
  return m("span", {
    className: "CanelaTranslated is-working",
    role: "status",
    "aria-label": label,
    title: label
  }, m("span", {
    className: "CanelaTranslated-button"
  }, m(Icon, {
    name: "fas fa-spinner fa-spin"
  })));
}
export default function installTranslation() {
  // A tradução pelo próprio modelo: aparece no post, nas citações e na prévia.
  override(Post.prototype, 'contentHtml', function (original) {
    const html = postTranslation(this);
    return html && showing('p', this.id()) ? html : original();
  });
  override(Discussion.prototype, 'title', function (original) {
    const title = titleTranslation(this);
    return title && showing('d', this.id()) ? title : original();
  });

  // Renomear parte do título original, nunca da tradução.
  override(DiscussionControls, 'renameAction', function (original, ...args) {
    return withOriginal(() => original(...args));
  });

  // O Post do core só se redesenha quando o modelo muda (SubtreeRetainer):
  // alternar original/tradução também precisa contar como mudança.
  extend(CommentPost.prototype, 'oninit', function () {
    this.subtree?.check(() => {
      const post = this.attrs.post;
      const discussion = post.number() === 1 ? post.discussion() : null;
      const title = discussion ? `${showing('d', discussion.id())}:${!!titleTranslation(discussion)}` : '';
      return `${showing('p', post.id())}:${!!postTranslation(post)}:${working.has(post.id())}:${title}`;
    });
  });
  extend(CommentPost.prototype, 'oncreate', function () {
    enqueue('p', this.attrs.post);
  });
  extend(CommentPost.prototype, 'onupdate', function () {
    enqueue('p', this.attrs.post);
  });

  // O 文A à esquerda da data (a ordem visual é do LESS). Na primeira postagem
  // ele vale também para o título do tópico: um ícone só, perto do que mudou,
  // em vez de outro solto na ponta da linha do título.
  extend(CommentPost.prototype, 'headerItems', function (items) {
    const post = this.attrs.post;
    if (!info() || this.isEditing?.()) return;
    const discussion = post.number() === 1 ? post.discussion() : null;
    const title = discussion && titleTranslation(discussion) ? discussion : null;
    const keys = [];
    if (postTranslation(post)) keys.push(`p${post.id()}`);
    if (title) keys.push(`d${title.id()}`);
    if (keys.length) {
      const [kind, id, locale] = postTranslation(post) ? ['p', post.id(), post.attribute('canelaLocale')] : ['d', title.id(), title.attribute('canelaTitleLocale')];
      items.add('canelaTranslated', indicator(kind, id, locale, keys), 1);
    } else if (working.has(post.id())) {
      items.add('canelaTranslated', translating(), 1);
    }
  });
  extend(DiscussionHero.prototype, 'oncreate', function () {
    enqueue('d', this.attrs.discussion);
  });
  extend(DiscussionListItem.prototype, 'oncreate', function () {
    if (info()?.titles) enqueue('d', this.attrs.discussion);
  });

  // O 文A do topo da linha do tempo: abre as
  // "Preferências de idioma do conteúdo" (idioma da interface, os que a
  // pessoa entende e a tradução automática). Cada postagem continua com o
  // 文A dela, que alterna só aquela.
  extend('flarum/forum/components/DiscussionPage', 'sidebarItems', function (items) {
    if (!info() || !this.discussion) return;
    const label = app.translator.trans('ramon-canela.forum.ai.translation.prefs.title');
    items.add('canelaContentLanguage', m(Tooltip, {
      text: label
    }, m(Button, {
      className: classList('Button Button--icon CanelaTranslationToggle', {
        active: autoTranslate()
      }),
      icon: "fas fa-language",
      "aria-label": label,
      onclick: () => app.modal.show(() => import(/* webpackChunkName: 'forum/features/ai/ContentLanguageModal', webpackMode: 'lazy-once' */ './ContentLanguageModal'))
    })), 79);
  });
}
flarum.reg.add('ramon-canela', 'forum/features/ai/translation', installTranslation);flarum.reg.addChunkModule('forum/features/ai/ContentLanguageModal', './src/forum/features/ai/ContentLanguageModal.tsx', 'ramon-canela', 'forum/features/ai/ContentLanguageModal');